import { useRef, useEffect } from 'react';
import '../css/video-intro.css';

export default function VideoIntro({ onDone }) {
  const overlayRef  = useRef(null);
  const videoRef    = useRef(null);
  const soundBtnRef = useRef(null);
  const skipBtnRef  = useRef(null);

  useEffect(() => {
    const overlay  = overlayRef.current;
    const v        = videoRef.current;
    const soundBtn = soundBtnRef.current;
    const skipBtn  = skipBtnRef.current;
    if (!v || !overlay) return;

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      overlay.classList.add('vi-fade');
      setTimeout(() => { overlay.style.display = 'none'; onDone?.(); }, 900);
    };

    // iOS Safari requires muted set imperatively (React JSX prop unreliable)
    v.muted = true;

    // Try to play immediately — autoPlay attribute handles mobile native pipeline
    // .play() call is a fallback for browsers that ignore the attribute
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(finish);
    };

    // Play as soon as any data is available
    if (v.readyState >= 2) {
      tryPlay();
    } else {
      v.addEventListener('loadedmetadata', tryPlay, { once: true });
      v.addEventListener('canplay',        tryPlay, { once: true });
    }

    // Safety net — skip after 10s if nothing plays
    const timeout = setTimeout(finish, 10000);
    v.addEventListener('playing', () => clearTimeout(timeout), { once: true });
    v.addEventListener('ended',   finish, { once: true });

    const onSound = () => {
      v.muted = false;
      if (soundBtn) soundBtn.style.display = 'none';
    };
    if (soundBtn) soundBtn.addEventListener('click', onSound);
    if (skipBtn)  skipBtn.addEventListener('click',  finish);

    return () => {
      clearTimeout(timeout);
      v.removeEventListener('loadedmetadata', tryPlay);
      v.removeEventListener('canplay',        tryPlay);
      v.removeEventListener('ended',          finish);
      if (soundBtn) soundBtn.removeEventListener('click', onSound);
      if (skipBtn)  skipBtn.removeEventListener('click',  finish);
    };
  }, [onDone]);

  return (
    <div ref={overlayRef} className="vi-overlay">
      {/*
        All four attributes required together for iOS Safari autoplay:
        autoPlay + muted + playsInline + preload
      */}
      <video
        ref={videoRef}
        className="vi-video"
        src="/Video1.mp4"
        autoPlay
        muted
        playsInline
        preload="auto"
        x-webkit-airplay="allow"
      />

      <div className="vi-bar">
        <span className="vi-bar-brand">
          AMKO<span>IQ</span>
          <span className="vi-bar-tag">Creative Agency</span>
        </span>
        <button ref={soundBtnRef} className="vi-sound-btn">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
          </svg>
          Sound on
        </button>
        <button ref={skipBtnRef} className="vi-bar-skip">
          Skip &nbsp;›
        </button>
      </div>
    </div>
  );
}
